import React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Filter } from "lucide-react";
import { getEmailViewFilters } from "../../lib/emailViewConfig";
import { AUDIENCE_FILTER_OPTIONS } from "../../lib/emailAudienceMockAdapter";

// DASH-EMAIL-004: maps Audience's four now-functional contextual filter
// keys (see emailViewConfig.js's EMAIL_VIEW_FILTER_CONFIG.audience) to
// their useEmailFilterState draft field/setter names and option list.
// "audience" (the 5th contextual entry) has no matching draft field —
// it stays an inert "Coming soon" row below, unchanged.
const AUDIENCE_FIELD_CONFIG = {
  lead_source: { draftKey: "leadSource", setter: "setLeadSource", options: AUDIENCE_FILTER_OPTIONS.leadSource, allLabel: "All Lead Sources" },
  acquisition_channel: {
    draftKey: "acquisitionChannel",
    setter: "setAcquisitionChannel",
    options: AUDIENCE_FILTER_OPTIONS.acquisitionChannel,
    allLabel: "All Channels",
  },
  geography: { draftKey: "geography", setter: "setGeography", options: AUDIENCE_FILTER_OPTIONS.geography, allLabel: "All Geographies" },
  lifecycle_state: {
    draftKey: "lifecycleState",
    setter: "setLifecycleState",
    options: AUDIENCE_FILTER_OPTIONS.lifecycleState,
    allLabel: "All Lifecycle States",
  },
};

// DASH-EMAIL-011a: Creative Analytics' one real contextual filter key (see
// emailViewConfig.js's EMAIL_VIEW_FILTER_CONFIG.creative). Only active
// when view === "creative" — no effect on Audience's or any other view's
// rendering below. Retires DASH-EMAIL-005b's CREATIVE_FIELD_CONFIG
// (template_variant) — that dimension had no governed equivalent and is
// removed along with the surface that used it.
//
// DASH-EMAIL-012A: `options` is deliberately omitted here (unlike
// AUDIENCE_FIELD_CONFIG's static mock-backed lists) — Angle's real option
// list now comes from the real Email Marketing catalog
// (getEmailMarketingAngles(), fetched once in EmailDashboardPanel.jsx) and
// is threaded down as the `angleOptions` prop, since a static import can no
// longer supply governed, tenant-scoped Angle identities. See the render
// function below for where this is substituted in.
const CREATIVE_ANALYTICS_FIELD_CONFIG = {
  angle: {
    draftKey: "angle",
    setter: "setAngle",
    allLabel: "All Angles",
  },
};

/**
 * EmailFiltersControl — Checkpoint 2 context-aware filter framework.
 *
 * Reads the current view's contextual filter *definitions* from
 * emailViewConfig (mock/config-driven, no P1 schema, no real option
 * source). Later checkpoints replace each inert row with a real control as
 * a view's data contract is characterized — this component's shape
 * (view -> list of {key,label}) does not need to change when that happens.
 *
 * Checkpoint 3.3: renders nothing when the active view has no contextual
 * filters configured, rather than showing a generic "Filters" button that
 * only opens to say "no additional filters for this view yet." Overview
 * already exposes Period/Environment directly in the header — a filters
 * button with nothing behind it added visual noise without function.
 * Checkpoint 3.4E: Program/Campaign/Broadcast's real controls live
 * entirely outside this component (EmailFilterBar), so this change has no
 * effect on them. A view that legitimately gets contextual filters
 * through this mechanism (e.g. Audience & Acquisition already does)
 * continues to render normally.
 *
 * DASH-EMAIL-004: Audience's four matched rows (see AUDIENCE_FIELD_CONFIG
 * above) were, at that checkpoint, real <Select> controls wired directly
 * to the shared filterState's draft. DASH-EMAIL-P1-IMPL-008/008A retired
 * all four (and Period/Environment alongside them, and this component's
 * own trigger for Audience specifically) — see emailViewConfig.js's
 * `audience.contextual: []` and EmailFilterBar.jsx's
 * EMAIL_FILTER_BAR_FIELDS.unavailable comment. AUDIENCE_FIELD_CONFIG
 * itself is left in place as unreachable follow-up debt, not deleted.
 *
 * DASH-EMAIL-011a: Creative Analytics' one matched row (angle, see
 * CREATIVE_ANALYTICS_FIELD_CONFIG above) becomes a real <Select> the same
 * way, committed by the SAME Apply button via
 * EMAIL_FILTER_BAR_FIELDS.creativeAnalytics. No effect on Audience or any
 * other view.
 *
 * DASH-EMAIL-006a: the trigger's badge now shows how many contextual
 * dimensions are currently ACTIVE (non-default), not the constant count
 * of available definitions — see countActiveContextualFilters above.
 */
// DASH-EMAIL-006a (FIL-1): counts contextual dimensions currently ACTIVE
// (non-default), not how many definitions exist for the view — the prior
// trigger badge showed contextualFilters.length (a constant per view,
// e.g. always "4" for Audience), giving no signal about whether
// filtering is actually in effect once the popover is closed. Mirrors
// the OR(draft, applied) semantic EmailFilterBar.jsx's
// hasHiddenActiveFilters already uses for Program/Campaign: a dimension
// counts as active if either the pending draft OR the currently-applied
// (currently-displayed) value is non-default — so the badge reflects
// "is this relevant to what's shown, or what's about to be shown."
function countActiveContextualFilters(view, filterState) {
  const config = view === "audience" ? AUDIENCE_FIELD_CONFIG : view === "creative" ? CREATIVE_ANALYTICS_FIELD_CONFIG : null;
  if (!config || !filterState?.draft || !filterState?.applied) return 0;
  const { draft, applied } = filterState;
  return Object.values(config).reduce((count, fieldConfig) => {
    const isActive = draft[fieldConfig.draftKey] !== "all" || applied[fieldConfig.draftKey] !== "all";
    return count + (isActive ? 1 : 0);
  }, 0);
}

export default function EmailFiltersControl({ view, filterState, angleOptions = [] }) {
  const contextualFilters = getEmailViewFilters(view);

  if (contextualFilters.length === 0) {
    return null;
  }

  const draft = filterState?.draft;
  const activeCount = countActiveContextualFilters(view, filterState);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 hover:text-slate-100"
          data-testid="email-filters-trigger"
        >
          <Filter className="h-4 w-4" />
          Filters
          {activeCount > 0 && (
            <span
              className="rounded-full bg-indigo-500/20 px-1.5 text-xs text-indigo-300"
              data-testid="email-filters-active-count"
            >
              {activeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 border-white/10 bg-slate-950 text-slate-100 ring-1 ring-white/10"
        data-testid="email-filters-content"
      >
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Contextual filters
        </p>
        <ul className="space-y-2" data-testid="email-filters-list">
          {contextualFilters.map((f) => {
            const fieldConfig =
              view === "audience"
                ? AUDIENCE_FIELD_CONFIG[f.key]
                : view === "creative"
                  ? CREATIVE_ANALYTICS_FIELD_CONFIG[f.key]
                  : null;

            if (fieldConfig && draft) {
              const setter = filterState[fieldConfig.setter];
              const options = view === "creative" && f.key === "angle" ? angleOptions : fieldConfig.options;
              return (
                <li
                  key={f.key}
                  className="flex flex-col gap-1.5 rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-300"
                  data-testid={`email-filter-${f.key}`}
                >
                  <span>{f.label}</span>
                  <Select value={draft[fieldConfig.draftKey]} onValueChange={setter}>
                    <SelectTrigger
                      className="w-full min-w-0 flex-1 bg-white/5 text-slate-100 border-white/10"
                      data-testid={`email-filter-${f.key}-select`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{fieldConfig.allLabel}</SelectItem>
                      {options.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </li>
              );
            }

            return (
              <li
                key={f.key}
                className="flex items-center justify-between rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-300"
                data-testid={`email-filter-${f.key}`}
              >
                {f.label}
                <span className="text-xs text-slate-500">Coming soon</span>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
